import { View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import ModalWrapper from '@/components/General/ModalWrapper';
import PathEditor from '@/components/FollowUp/Paths/PathEditor';
import { Query } from '@/utils/react-query/Queries';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule } from '@/utils/organizationEntitlements';
import { showAlert } from '@/utils/showAlert';

/**
 * /follow-up/paths/new — create a flat Path template (§25.5; Manage-gated,
 * publish via follow_up_publish_path is the sole write path). There is no
 * type picker — Care starts without a template via follow_up_start_care.
 * On success the new template's detail replaces this route so Back lands
 * on the list.
 */
export default function FollowUpPathCreateScreen() {
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();

    const organizationID = loggedInUser?.organizationID;
    const canManage = !!loggedInUser?.can?.('Manage Follow Up');
    const moduleAllowed = canViewModule(entitlements, 'paths');
    const confirmDiscard = () => {
        showAlert({
            title: 'Discard changes?',
            subtitle: 'Your unpublished edits will be lost.',
            cancelText: 'Keep Editing',
            confirmText: 'Discard',
            isDestructive: true,
            onConfirm: () => router.back(),
        });
        return false;
    };

    if (loggedInUser && (!canManage || !moduleAllowed)) {
        return (
            <ModalWrapper title="New Path" cancelText="Close">
                <View style={styles.deniedWrap}>
                    <EmptyListPlaceholder
                        type="404"
                        spacing={40}
                        message={!moduleAllowed
                            ? 'Follow Up is not available on this plan'
                            : 'Creating paths requires the Manage Follow Up permission.'}
                    />
                </View>
            </ModalWrapper>
        );
    }

    return (
        <ModalWrapper title="New Path" cancelText="Cancel" onBeforeClose={confirmDiscard} width={700} noPadding>
            <PathEditor
                organizationID={organizationID}
                path={null}
                onCancel={() => router.back()}
                onPublished={(data) => {
                    if (data?.pathID) {
                        router.replace(`/follow-up/paths/${data.pathID}`);
                    } else {
                        router.back();
                    }
                }}
            />
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(() => ({
    deniedWrap: { flex: 1, justifyContent: 'center' },
}));
